import { DatafeedSocket } from '../../bot/adapters/datafeedSocket'
import { fetchCandlesBefore } from '../../bot/adapters/candleSocket'
import { fetchBacktestEvents } from '../../bot/adapters/eventFetcher'
import type { CandleData } from '../../bot/types/chartData'
import {
  displaySpan,
  PAIR_WINDOW_BARS,
  VISIBLE_LEAD_BARS,
  type DisplaySpan,
  type DrawingSpan,
  type DrawnEntry,
} from './walkthroughPeriod'

export type WalkthroughDisplay = DisplaySpan

export interface PrepareWalkthroughDisplayOptions {
  btid: string
  symbol: string
  timeframe: string
  span: DrawingSpan
  /** The drawn entries — see drawnEntries. */
  entries: DrawnEntry[]
  /** The run's REAL range, unix seconds. */
  rangeStartTs: number
  rangeEndTs: number
}

/**
 * Work out the window around the drawings for a FINISHED run — the stronger of the two tinted
 * sections on the canvas, and where the replay starts.
 *
 * Only possible once the run is complete: the window's end depends on where the code actually
 * traded (see `displaySpan`). Two requests, in parallel:
 *
 *  - the run's order events over its REAL range — the same request the trades pane makes, so
 *    it answers from the event cache there;
 *  - the candles up to the first drawing. They place the window's first bar on a real candle —
 *    up to PAIR_WINDOW_BARS before the first drawing, when the code's trade for it came early.
 *
 * Never rejects. Either request failing degrades rather than blocks: no events means a window
 * that runs to the run's end, no candles means a wall-clock lead — both still a working replay.
 */
export async function prepareWalkthroughDisplay({
  btid,
  symbol,
  timeframe,
  span,
  entries,
  rangeStartTs,
  rangeEndTs,
}: PrepareWalkthroughDisplayOptions): Promise<WalkthroughDisplay> {
  const firstDrawingSec = Math.floor(span.fromTs / 1000)
  const [orders, before] = await Promise.all([
    fetchBacktestEvents(btid, rangeStartTs, rangeEndTs),
    candlesBefore(
      symbol,
      timeframe,
      firstDrawingSec,
      PAIR_WINDOW_BARS + VISIBLE_LEAD_BARS + 1,
    ),
  ])

  return displaySpan(span, timeframe, rangeStartTs, rangeEndTs, { orders, entries, candles: before })
}

/** `count` bars at or before `toTs`, or none — see prepareWalkthroughDisplay for why none is fine. */
async function candlesBefore(
  symbol: string,
  timeframe: string,
  toTs: number,
  count: number,
): Promise<CandleData[]> {
  let socket: DatafeedSocket | null = null
  try {
    socket = new DatafeedSocket()
    return await fetchCandlesBefore(socket, symbol, timeframe, toTs, count)
  } catch {
    return []
  } finally {
    socket?.dispose()
  }
}
